import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationExtras, Router, UrlTree } from '@angular/router';
import * as moment from 'moment-timezone';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { EventService } from 'src/app/services/event.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { AppUtils } from 'src/app/shared/app.utils';
import { PhoneNumberFormatPipe } from 'src/app/shared/pipes/phone-number-format.pipe';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-event-detail',
  templateUrl: './event-detail.component.html',
  styles: [".col-lg-4.col-md-5 { text-align: right; }", ".col-lg-8.col-md-7 { font-weight: 700; }"],
  providers: [PhoneNumberFormatPipe]
})
export class EventDetailComponent implements OnInit {
  @Input() event: ServerModels.ENESS_EventDTO;
  @Input() importItemId: string = '';
  @Input() title = "Event details";
  @Input() returnRoute: { commands: any[], extras: NavigationExtras } = null;
  @Output() showEventDetail = new EventEmitter<boolean>();

  apiRoot: string = environment.apiEndpoint;
  displayErrorStatus: boolean = false;
  errorMessage: string = 'An error occurred.';
  eventNarrative: any = {};
  eventNarrative$: Observable<string>;
  securityContext = SecurityContext;
  showEditButton: boolean = false;
  showEventNarrative: boolean = false;
  userTimeZone: string;

  private _unsubscribe$: Subject<void> = new Subject<void>();
  constructor(private eventService: EventService,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private phoneNumberFormat: PhoneNumberFormatPipe,
    public elementRef: ElementRef,
    public domSanitizer: DomSanitizer
  ) { }

  private _selectedFile: ServerModels.ENESS_EventAttachmentsDTO;
  get selectedFile(): ServerModels.ENESS_EventAttachmentsDTO {
    return this._selectedFile;
  }

  @Input() set hasEditPermission(hasEditPermission: boolean) {
    this.showEditButton = hasEditPermission || false;
  }

  ngOnInit() {
    this.showEventNarrative = false;
    this.userTimeZone = moment.tz.zone(moment.tz.guess()).abbr(new Date().getTimezoneOffset());

    window.setTimeout(() => {
      (<HTMLElement>this.elementRef.nativeElement).setAttribute("tabindex", "-1");
      (<HTMLElement>this.elementRef.nativeElement).focus();
    }, 1000);

    if (!!this.event) {
      this.eventService.hasEditPermission(this.event.EventID).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (canEditEvent:boolean) => {
          this.hasEditPermission = canEditEvent;
        }
      });
    } 
  }

  public editEvent(): void {
    if (this.returnRoute) {
      let returnUrlTree: UrlTree = this.router.createUrlTree(this.returnRoute.commands, this.returnRoute.extras);
      this.previousRouteService.returnUrl = this.router.serializeUrl(returnUrlTree);
    } else {
      this.previousRouteService.returnUrl = this.router.url;
    }
    if (!!this.importItemId) {
      this.eventService.generateEventStagingId().subscribe((newId:string) => this.router.navigate(['event-stepper', { sid: newId, id: '', ipid: this.importItemId }], { state: { 'mode': 'Edit' } }));
    } else {
      this.eventService.setEventStagingData(this.event.EventID).subscribe((newId: string) => {
        this.router.navigate(['event-stepper', { sid: newId, id: this.event.EventID, ipid: '' }], { state: { 'mode': 'Edit' } });
      });
    }
  }

  public formatLEPartner(lePartner: ServerModels.ENESS_XREF_EventsLEPartnersDTO): string {
    if (!lePartner || !lePartner.ENESS_LEPartner) {
      return '';
    }

    let displayString: string = `${(lePartner.ENESS_LEPartner?.FirstName || '').trim()} ${(lePartner.ENESS_LEPartner?.MiddleName || '').trim()} ${(lePartner.ENESS_LEPartner?.LastName || '').trim()} ${!!lePartner.PhoneNumber ? 'Ph. ' + this.phoneNumberFormat.transform(lePartner.PhoneNumber) : ''}`;

    return displayString;
  }

  public loadNarrative(): void {
    this.eventNarrative$ = this.eventService.getNarrativeById(this.event.EventID).pipe(tap((narrative: string) => {
      this.eventNarrative = {
        LECaseNumber: this.event.ENESS_XREF_LECaseNumbers.LECaseNumber,
        Narrative: this.domSanitizer.sanitize(SecurityContext.HTML, this.domSanitizer.bypassSecurityTrustHtml(narrative)) 
      }
    }));
    this.showEventNarrative = true;
  }

  public onClickDownload(file: ServerModels.ENESS_XREF_EventAttachmentsDTO): void {
    if (!file) {
      return;
    }
    this.errorMessage = '';
    this.displayErrorStatus = false;
    this.eventService.getAttachmentById(this.event.EventID, file.AttachmentID).subscribe(
      (attachment: Blob) => {
        AppUtils.embedDownloadLink(attachment, file.FileName);
      },
      (err:any) => {
        this.errorMessage = `${err} Unable to retrieve requested attachment.`;
        this.displayErrorStatus = true;
      }
    )
  }
}
